import type { ReactElement } from "react";
import { useQueries } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { apiClient } from "../api/client";
import type { CatalogEntry } from "../api/types";
import { useCompare } from "../contexts/CompareContext";
import { SuiteBadge } from "../components/SuiteBadge";

/**
 * Cycle 9 STORY-802: Compare products page.
 *
 * Route: /compare
 * Reads selected slugs from CompareContext (max 3).
 * Renders matrix: products as columns, features as rows.
 */

const FEATURE_ROWS: { key: keyof CatalogEntry | "actions"; label: string }[] = [
  { key: "tagline", label: "Краткое описание" },
  { key: "suite", label: "Suite (направление)" },
  { key: "demo_status", label: "Статус demo" },
  { key: "industry", label: "Отрасли" },
  { key: "icp", label: "Целевые ICP" },
  { key: "tags", label: "Технологии" },
  { key: "roi_metrics", label: "ROI / метрики" },
  { key: "actions", label: "Действия" },
];

export function CompareProductsPage(): ReactElement {
  const { selectedSlugs, removeProduct, clear } = useCompare();

  const queries = useQueries({
    queries: selectedSlugs.map((slug) => ({
      queryKey: ["product-detail", slug],
      queryFn: async () => {
        const { data } = await apiClient.get<CatalogEntry>(`/api/catalog/${slug}`);
        return data;
      },
    })),
  });

  const products = queries
    .map((q) => (q.data ? q.data : null))
    .filter((p): p is CatalogEntry => p !== null);

  const isLoading = queries.some((q) => q.isLoading);
  const hasError = queries.some((q) => q.error);

  if (selectedSlugs.length === 0) {
    return (
      <section className="compare-page compare-page--empty">
        <h1>Сравнение продуктов</h1>
        <p>
          Выберите 2–3 продукта для side-by-side сравнения. Откройте{" "}
          <Link to="/catalog">каталог</Link> и нажмите «В сравнение» на карточке.
        </p>
      </section>
    );
  }

  return (
    <section className="compare-page">
      <header className="compare-page__header">
        <Link to="/catalog" className="compare-page__back">
          ← В каталог
        </Link>
        <h1>Сравнение продуктов</h1>
        <p className="compare-page__count">
          Выбрано: {selectedSlugs.length} из 3
          {selectedSlugs.length > 0 && (
            <button className="compare-page__clear" type="button" onClick={() => clear()}>
              Очистить
            </button>
          )}
        </p>
      </header>

      {isLoading && <p className="compare-page__loading">Загрузка…</p>}
      {hasError && <p className="compare-page__error">Не удалось загрузить часть продуктов.</p>}

      {!isLoading && !hasError && products.length > 0 && (
        <div className="compare-page__table-wrap">
          <table className="compare-table">
            <thead>
              <tr>
                <th className="compare-table__row-label">Параметр</th>
                {products.map((p) => (
                  <th key={p.slug} className="compare-table__product-header">
                    <div className="compare-table__product-card">
                      <SuiteBadge suite={p.suite} />
                      <h3 className="compare-table__product-name">{p.name}</h3>
                      <button
                        className="compare-table__remove"
                        onClick={() => removeProduct(p.slug)}
                        aria-label={`Убрать ${p.name} из сравнения`}
                      >
                        × Убрать
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_ROWS.map((row) => (
                <tr key={String(row.key)}>
                  <th className="compare-table__row-label" scope="row">
                    {row.label}
                  </th>
                  {products.map((p) => (
                    <td
                      key={p.slug}
                      className="compare-table__cell"
                      style={{ borderTopColor: p.accent_color }}
                    >
                      {renderCell(p, row.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <footer className="compare-page__cta">
        <Link to="/request-demo" className="btn-primary">
          Запросить демонстрацию выбранных продуктов →
        </Link>
        <Link to="/catalog" className="btn-secondary">
          Добавить ещё продукты
        </Link>
      </footer>
    </section>
  );
}

function renderCell(p: CatalogEntry, key: keyof CatalogEntry | "actions"): ReactElement {
  if (key === "actions") {
    return (
      <div className="compare-table__actions">
        <Link
          to={`/product/${p.slug}`}
          className="btn-secondary btn-small"
          style={{ borderColor: p.accent_color, color: p.accent_color }}
        >
          Подробнее
        </Link>
        {p.demo_status === "live" && (
          <Link
            to={`/demo/${p.slug}`}
            className="btn-primary btn-small"
            style={{ backgroundColor: p.accent_color }}
          >
            Demo →
          </Link>
        )}
      </div>
    );
  }

  if (key === "tagline") return <span>{p.tagline}</span>;
  if (key === "suite") return <span>{p.suite}</span>;
  if (key === "demo_status") {
    return (
      <span className={`compare-table__status compare-table__status--${p.demo_status}`}>
        {p.demo_status === "live" ? "✓ Live" : "🚧 Скоро"}
      </span>
    );
  }
  if (key === "industry") {
    return (
      <ul className="compare-table__chips">
        {p.industry.map((ind) => (
          <li key={ind}>{ind}</li>
        ))}
      </ul>
    );
  }
  if (key === "icp") {
    return (
      <ul className="compare-table__chips">
        {p.icp.map((c) => (
          <li key={c} title={`ICP персона ${c}`}>
            {c}
          </li>
        ))}
      </ul>
    );
  }
  if (key === "tags") {
    return (
      <ul className="compare-table__chips compare-table__chips--muted">
        {p.tags.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
    );
  }
  if (key === "roi_metrics") {
    return (
      <ul className="compare-table__roi">
        {p.roi_metrics.slice(0, 3).map((m) => (
          <li key={m.metric}>
            <strong style={{ color: p.accent_color }}>{m.value}</strong> — {m.metric}
          </li>
        ))}
      </ul>
    );
  }
  return <span>—</span>;
}
